import { useRef, useState } from "react";
import Layout from "../components/Layout";
import CountdownTimer from "../components/CountdownTimer";
import ShareButton from "../components/ShareButton";
import DownloadImageButton from "../components/DownloadImageButton";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Play, RotateCcw, Briefcase } from "lucide-react";

const COLOR = "#EF4444";
const DURATION = 120;

const DIF_STYLE = {
  "FÁCIL": { bg: "#22C55E", fg: "#1a1a1a" },
  "DESAFIANTE": { bg: "#FFB000", fg: "#1a1a1a" },
  "IMPOSIBLE": { bg: "#EF4444", fg: "#fff" },
};

const Skeleton = () => (
  <div className="bg-white border-4 border-[#1a1a1a] rounded-3xl shadow-hard-md p-8 space-y-4 animate-pulse">
    <div className="h-6 w-32 bg-[#e5e1d5] rounded" />
    <div className="h-4 w-full bg-[#e5e1d5] rounded" />
    <div className="h-4 w-5/6 bg-[#e5e1d5] rounded" />
    <div className="h-4 w-2/3 bg-[#e5e1d5] rounded" />
  </div>
);

const ClienteImposible = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | ready | running | done
  const cardRef = useRef(null);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await api.post("/generate/cliente-imposible", {});
      setData(res.data);
      setPhase("ready");
    } catch {
      toast.error("Ups, algo salió mal. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const start = () => setPhase("running");
  const finish = () => setPhase("done");
  const reset = () => { setData(null); setPhase("idle"); };

  const d = data?.data || {};
  const dif = d.dificultad || "DESAFIANTE";
  const difStyle = DIF_STYLE[dif] || DIF_STYLE["DESAFIANTE"];

  return (
    <Layout accent={COLOR}>
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pt-10 sm:pt-16">
        <div className="mb-8">
          <span className="inline-block font-mono text-xs uppercase tracking-[0.25em] font-bold bg-[#EF4444] text-white border-4 border-[#1a1a1a] rounded-full px-4 py-1 mb-4">
            tool · 05 · nuevo
          </span>
          <h1 className="font-display font-black text-5xl sm:text-7xl leading-[0.95] tracking-tight mb-4">
            cliente <span className="text-[#EF4444]">imposible</span>.
          </h1>
          <p className="font-mono text-sm sm:text-base text-[#4a4a4a] max-w-xl">
            La IA inventa el brief más absurdo y contradictorio. Tienes 2 minutos para sketchar una solución.
          </p>
        </div>

        {phase === "idle" && (
          <div className="pop-in">
            <button
              data-testid="cliente-generate-btn"
              onClick={generate}
              disabled={loading}
              className="press w-full sm:w-auto bg-[#EF4444] text-white border-4 border-[#1a1a1a] rounded-2xl px-8 py-5 font-display font-black text-2xl shadow-hard-md disabled:opacity-60 inline-flex items-center gap-2"
            >
              <Briefcase className="w-6 h-6" strokeWidth={3} />
              {loading ? "Generando..." : "¡Nuevo cliente!"}
            </button>
          </div>
        )}

        {loading && phase !== "ready" && phase !== "running" && phase !== "done" && (
          <div className="mt-6"><Skeleton /></div>
        )}

        {phase === "ready" && data && (
          <div ref={cardRef} className="bg-[#FFFBEB] border-4 border-[#1a1a1a] border-l-[12px] border-l-[#22C55E] rounded-3xl shadow-hard-lg p-6 sm:p-10 pop-in relative" data-testid="cliente-ready">
            <div className="absolute top-3 right-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#737373] font-bold">
              guango · cliente imposible
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <span className="font-mono text-xs uppercase tracking-[0.25em] font-bold text-[#737373]">brief oficial</span>
              <span
                data-testid="dificultad-badge"
                className="inline-block font-mono text-xs uppercase tracking-[0.2em] font-black border-4 border-[#1a1a1a] rounded-full px-4 py-1 shadow-hard-sm"
                style={{ background: difStyle.bg, color: difStyle.fg }}
              >
                {dif}
              </span>
            </div>

            <div className="font-mono text-xs uppercase tracking-[0.2em] font-bold mb-2 text-[#737373]">El cliente dice:</div>
            <blockquote className="font-display italic text-xl sm:text-2xl text-[#1a1a1a] leading-snug border-l-4 border-[#1a1a1a] pl-4 mb-6">
              &ldquo;{d.brief}&rdquo;
            </blockquote>

            <div className="font-mono text-xs uppercase tracking-[0.2em] font-bold mb-2 text-[#737373]">Lo que realmente quiere:</div>
            <p className="font-mono text-sm sm:text-base text-[#1a1a1a] leading-relaxed mb-8">
              {d.interpretacion}
            </p>

            <div className="flex flex-wrap gap-3" data-exclude-from-image="true">
              <button
                data-testid="cliente-start-btn"
                onClick={start}
                className="press inline-flex items-center gap-2 bg-[#22c55e] border-4 border-[#1a1a1a] rounded-xl px-6 py-3 font-display font-black text-xl shadow-hard-md"
              >
                <Play className="w-5 h-5" strokeWidth={3} /> iniciar 2:00
              </button>
              <button
                data-testid="cliente-regen-btn"
                onClick={generate}
                disabled={loading}
                className="press inline-flex items-center gap-2 bg-white border-4 border-[#1a1a1a] rounded-xl px-6 py-3 font-mono text-sm uppercase tracking-wider font-bold shadow-hard-sm"
              >
                otro cliente
              </button>
              <ShareButton promptId={data.id} color={COLOR} testId="cliente-share" />
              <DownloadImageButton
                targetRef={cardRef}
                fileName={`guango-cliente-imposible-${data.id.slice(0, 8)}.png`}
                color={COLOR}
                testId="cliente-download"
              />
            </div>
          </div>
        )}

        {phase === "running" && (
          <div data-testid="cliente-running">
            <div className="bg-[#1a1a1a] text-white border-4 border-[#1a1a1a] rounded-2xl px-5 py-3 inline-block font-mono text-xs uppercase tracking-[0.25em] font-bold mb-6 max-w-full">
              brief: {d.brief?.slice(0, 80)}{d.brief?.length > 80 ? "..." : ""}
            </div>
            <CountdownTimer duration={DURATION} color={COLOR} running format="minutes" warnAt={30} onComplete={finish} />
          </div>
        )}

        {phase === "done" && (
          <div className="bg-[#EF4444] text-white border-4 border-[#1a1a1a] rounded-3xl shadow-hard-lg p-8 sm:p-12 pop-in" data-testid="cliente-done">
            <div className="font-display font-black text-5xl sm:text-7xl mb-4">¡tiempo!</div>
            <p className="font-mono text-base sm:text-lg mb-2">¿Cómo quedó? 👀</p>
            <p className="font-mono text-sm mb-8 text-white/80">Comparte tu sketch o intenta otro cliente imposible.</p>
            <div className="flex flex-wrap gap-3">
              <button
                data-testid="cliente-another-btn"
                onClick={generate}
                disabled={loading}
                className="press inline-flex items-center gap-2 bg-[#1a1a1a] border-4 border-[#1a1a1a] rounded-xl px-6 py-3 font-display font-black text-xl shadow-hard-md"
              >
                <RotateCcw className="w-5 h-5" strokeWidth={3} /> otro cliente
              </button>
              <button
                data-testid="cliente-reset-btn"
                onClick={reset}
                className="press inline-flex items-center gap-2 bg-white text-[#1a1a1a] border-4 border-[#1a1a1a] rounded-xl px-6 py-3 font-mono text-sm uppercase tracking-wider font-bold shadow-hard-sm"
              >
                volver al inicio
              </button>
              {data && <ShareButton promptId={data.id} color="#fff" testId="cliente-done-share" />}
            </div>
          </div>
        )}
      </section>
    </Layout>
  );
};

export default ClienteImposible;
